import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import getlistCategory from "../../../utils/getCategoryList";
import translations from "../../../assets/locals/translations";
import LabelImportantIcon from "@mui/icons-material/LabelImportant";
import { InputAdornment, TextField, Tooltip, Button, Menu, MenuItem } from "@mui/material";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";
import { useSelector } from "react-redux";
import "../../../styles/Users.css";
import { useLocation, useNavigate } from "react-router-dom";
import DataGridTable from "../../table/DataGridTable";
import ToolPanel from "../../table/panel/ToolPanel";
import { FadeLoader } from "react-spinners";
import DeleteBox from "../../table/panel/DeleteBox";
import MovetoStageMenu from "../../stages/MovetoStageMenu";
import GeneralApiService from "../../../services/generalServices";
// IMPORTS FOR CUSTOM TOOLBAR
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector
} from "@mui/x-data-grid";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import { usePaginationState } from "../../../hooks/usePaginationState";
import PersistentDataGrid from "../../table/PersistentDataGrid";

// =========================================================================
// CUSTOM TOOLBAR COMPONENT
// =========================================================================
const CustomToolbar = ({
  onExportFunc,
  isDataLoading,
  totalCount
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadClick = () => {
    handleClose();
    if (onExportFunc) onExportFunc();
  };

  const handlePrintClick = () => {
    handleClose();
    window.print();
  };

  return (
    <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />

      {/* Export Button */}
      <Button
        size="small"
        startIcon={<FileDownloadIcon />}
        onClick={handleClick}
        disabled={isDataLoading || totalCount === 0}
        sx={{
          color: 'primary.main',
          textTransform: 'uppercase',
          fontSize: '0.8125rem',
          padding: '4px 5px'
        }}
      >
        {isDataLoading ? "EXPORTING..." : "EXPORT"}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem onClick={handleDownloadClick} sx={{ fontSize: '0.9rem' }}>
          <FileDownloadIcon sx={{ mr: 1, fontSize: '1.2rem' }} /> Download as CSV
        </MenuItem>
        <MenuItem onClick={handlePrintClick} sx={{ fontSize: '0.9rem' }}>
          <PrintIcon sx={{ mr: 1, fontSize: '1.2rem' }} /> Print
        </MenuItem>
      </Menu>

    </GridToolbarContainer>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const EnquiryStage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const generalServices = new GeneralApiService();
  const ev_stage = location.state?.stage || null;
  const currentLanguage = useSelector((state) => state.language.language);
  const [enquiries, setEnquiries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Initialize selectedDates from sessionStorage or empty array
  const [selectedDates, setSelectedDates] = useState(() => {
    try {
      const saved = sessionStorage.getItem("lostEnquiry_selectedDates");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Initialize selectedType from sessionStorage or "All"
  const [selectedType, setSelectedType] = useState(() => {
    try {
      const saved = sessionStorage.getItem("lostEnquiry_selectedType");
      return saved || "All";
    } catch {
      return "All";
    }
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [isDataLoading, setIsDataLoading] = useState(false);

  const [paginationModel, setPaginationModel] = usePaginationState(
    "enquiryTablePagination",
    { pageSize: 50, page: 0 }
  );

  const { page, pageSize } = paginationModel;

  const [modalData, setModalData] = useState({
    show: false,
    rowData: null,
    endPoint: null,
  });
  const [MovetoStageModal, setMovetoStageModal] = useState({
    show: false,
    rowData: null,
    stages: [],
  });
  const [Category, setCategory] = useState(ev_stage.value || 2);
  const [rowData, setRowData] = useState([]);
  const [excelKey, setExcelKey] = useState(null);
  const [excelColumns, setExcelColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAvailable, setIsAvailable] = useState({
    DELIVERY: false,
    LOST: false,
    INVALID: false,
    OTHER: false,
  });
  const [xlsxFile, setXlsxFile] = useState(null);

  // ==========================================
  // FIXED COLUMN VISIBILITY LOGIC
  // ==========================================
  const [sourceMap, setSourceMap] = useState({});

  // Fetch source master data on mount
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_NODE_URL}/api/source-list`, {
      headers: { token: localStorage.getItem("rbacToken") }
    }).then(res => {
      // Convert array to map: { 11: "Walk-in", ... }
      const map = {};
      if (Array.isArray(res.data)) {
        res.data.forEach(item => { map[item.id] = item.name; });
      }
      setSourceMap(map);
    }).catch(() => { });
  }, []);
  // Single state to hold visibility for current active stage
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  // Effect: Load Saved Column Settings when Stage Changes
  useEffect(() => {
    const stageKey = ev_stage?.type || "DEFAULT";
    const storageKey = `enquiry_columns_${stageKey}`; // Unique Key per stage

    try {
      const savedModel = localStorage.getItem(storageKey);
      if (savedModel) {
        setColumnVisibilityModel(JSON.parse(savedModel));
      } else {
        setColumnVisibilityModel({}); // Reset to default if no saved settings
      }
    } catch (error) {
      console.error("Error loading column visibility:", error);
      setColumnVisibilityModel({});
    }
  }, [ev_stage?.type]);

  // Handler: Save Column Settings when User Changes them
  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    const stageKey = ev_stage?.type || "DEFAULT";
    const storageKey = `enquiry_columns_${stageKey}`;
    localStorage.setItem(storageKey, JSON.stringify(newModel));
  };


  let isType = ["DELIVERY", "INVALID", "LOST"];

  // ==========================================
  // EXPORT LOGIC
  // ==========================================

  const downloadCSV = (data, fileName) => {
    let currentColumns = [];
    if (isType.includes(ev_stage.type)) {
      currentColumns = columns[ev_stage.type];
    } else {
      currentColumns = columns.OTHER;
    }

    const exportColumns = currentColumns.filter(col => col.field !== 'menu' && col.field !== 'action');
    const headers = exportColumns.map(col => `"${col.headerName}"`).join(",");

    const csvRows = data.map(row => {
      return exportColumns.map(col => {
        let value = row[col.field];

        if (col.field.includes('date') || col.field === 'retail_date' || col.field === 'delivery_date') {
          if (value && moment(value).isValid()) {
            value = moment(value).format("DD-MM-YYYY");
          } else {
            value = "";
          }
        }

        if (col.field.includes('price')) {
          value = value || 0;
        }

        const stringValue = String(value || "").replace(/"/g, '""');
        return `"${stringValue}"`;
      }).join(",");
    });

    const csvString = [headers, ...csvRows].join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportAll = async () => {
    try {
      setIsDataLoading(true);

      const customDates = getAllDates(selectedDates);
      let startDateStr = "";
      let endDateStr = "";

      if (selectedType === "All") {
        startDateStr = "";
        endDateStr = "";
      } else if (selectedType === "Custom") {
        startDateStr = customDates[0];
        endDateStr = customDates[customDates.length - 1];
      } else {
        startDateStr = selectedDates[0];
        endDateStr = selectedDates[selectedDates.length - 1];
      }

      const payload = {
        ev_stage: ev_stage.type,
        category: Category,
        page: 0,
        limit: totalCount > 0 ? totalCount : 5000,
        searchQuery: searchTerm,
        ...(searchTerm.trim() === "" && { startDate: startDateStr, endDate: endDateStr }),
      };

      const response = await generalServices.getSalesData(payload);

      if (response && response.isSuccess) {
        const result = response.result;
        if (result && result[ev_stage.type]) {
          const enrichedData = result[ev_stage.type].map((item) => {
            let customFields = {};
            try {
              customFields = item.custom_fields ? JSON.parse(item.custom_fields) : {};
            } catch (error) {
              console.error("Error parsing custom_fields:", error);
            }
            return { ...item, ...customFields };
          });

          downloadCSV(enrichedData, `Enquiry_Data_${ev_stage.type}_${moment().format('YYYY-MM-DD')}`);
        } else {
          alert("No data found to export.");
        }
      } else {
        alert("Failed to fetch data for export.");
      }
    } catch (error) {
      console.error("Export Error:", error);
      alert("Error exporting data.");
    } finally {
      setIsDataLoading(false);
    }
  };


  const editEnquiryCell = (editEnquiryData) => {
    const formattedType = formatType(ev_stage.type);
    navigate(`/sale/data/${formattedType}/edit`, {
      state: { ev_stage: ev_stage, editData: editEnquiryData },
    });
  };
  const getStageColumns = () => {
    if (ev_stage.type === "SSP") return columns.SSP;
    if (ev_stage.type === "DELIVERY") return columns.DELIVERY;
    if (ev_stage.type === "INVALID") return columns.INVALID;
    if (ev_stage.type === "LOST") return columns.LOST;
    return columns.OTHER;
  };

  const deleteActionCall = async (row) => {
    setModalData({
      show: true,
      rowData: row,
      endPoint: `general/delete-stage-row/${ev_stage.type}`,
    });
  };

  const sspActionsColumn = {
    field: "menu",
    headerName: (
      <FontAwesomeIcon icon={faEllipsisV} style={{ marginRight: "15px" }} />
    ),
    className: "bg-dark",
    sortable: false,
    filterable: false,
    headerAlign: "right",
    align: "right",
    disableColumnMenu: true,
    width: 120,
    renderCell: (params) => (
      <div className="d-flex justify-content-center dotHoverempicon">
        <FontAwesomeIcon icon={faEllipsisV} />
        <div className="expandDiv expandDivenq">
          <Tooltip title="Edit">
            <button
              type="button"
              className="myActionBtn m-1"
              onClick={() => editEnquiryCell(params.row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                className="bi bi-pencil-square"
                viewBox="0 0 16 16"
              >
                <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                <path
                  fillRule="evenodd"
                  d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"
                />
              </svg>
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button
              type="button"
              onClick={() => deleteActionCall(params.row)}
              className="myActionBtn m-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                className="bi bi-trash3"
                viewBox="0 0 16 16"
              >
                <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
              </svg>
            </button>
          </Tooltip>
        </div>
      </div>
    ),
  };
  const onDeleteResponse = async (response) => {
    if (response.success) {
      // If we are deleting the last item on the current page, move back one page
      if (enquiries.length === 1 && page > 0) {
        setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }));
        // The useEffect watching `page` will automatically trigger the fetch
      } else {
        // Otherwise, just refresh the data for the current page
        await fetchSalesData(page, pageSize, selectedDates, selectedType, searchTerm);
      }
    }
  };

  const formatType = (type) =>
    type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();

  const handleStageClick = (selectedStage, row) => {
    let stages = [
      { label: "Delivered", type: "DELIVERY", color: "#6BCB77", value: 2 },
      { label: "Invalid", type: "INVALID", color: "#9E9E9E", value: 2 },
      { label: "Lost", type: "LOST", color: "#F56C6C", value: 2 },
    ];

    let updatedStages = stages.filter((stage) => stage.type !== selectedStage);

    setMovetoStageModal({
      show: true,
      rowData: row,
      stages: updatedStages,
    });
  };

  const handleStageMove = async (stage, rowData) => {
    try {
      const formattedType = formatType(stage.type);
      navigate(`/sale/data/${formattedType}/edit`, {
        state: { ev_stage: stage, editData: rowData },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const [columns, setColumns] = useState({
    SSP: [
      // {
      //   field: "id",
      //   headerName: "N.",
      //   width: 80,
      //   valueGetter: (params) => {
      //     const index = params.api.getAllRowIds().indexOf(params.id);
      //     return (page * pageSize) + index + 1;
      //   },
      // },
      { field: "first_name", headerName: "Name", minWidth: 150, flex: 1 },
      { field: "phone_number", headerName: "Mobile Number", minWidth: 150, flex: 1 },
      { field: "whatsapp_number", headerName: "WhatsApp Number", minWidth: 150, flex: 1 },
      { field: "district", headerName: "District", minWidth: 100, flex: 1 },
      { field: "taluka", headerName: "Taluka", minWidth: 100, flex: 1 },
      { field: "village", headerName: "Village", minWidth: 100, flex: 1 },
      { field: "primary_source", headerName: "Primary Source", minWidth: 120, flex: 1 },
      { field: "enquiry_source", headerName: "Enquiry Source", minWidth: 120, flex: 1 },
      { field: "farm_name", headerName: "Farm Name", minWidth: 120, flex: 1 },
      { field: "remarks", headerName: "Remarks", minWidth: 120, flex: 1 },
      { field: "wark", headerName: "Wark", minWidth: 120, flex: 1 },
      // Old Product Details
      { field: "old_make", headerName: "Old Make", minWidth: 120, flex: 1 },
      { field: "old_model", headerName: "Old Model", minWidth: 120, flex: 1 },
      { field: "old_year", headerName: "Old Year", minWidth: 100, flex: 1 },
      { field: "old_price", headerName: "Old Price", minWidth: 120, flex: 1 },
    ],
    DELIVERY: [
      {
        field: "first_name",
        headerAlign: "left",
        align: "left",
        headerName: translations[currentLanguage].firstname,
        minWidth: 310,
        flex: 1,
        renderCell: (params) => {
          const firstName = params.row.first_name || " ";
          return (
            <div
              className="text-uppercase"
              style={{
                textDecoration: "underline",
                color: "#000102",
                cursor: "pointer",
              }}
              onClick={() => {
                editEnquiryCell(params.row);
              }}
            >
              {firstName}
            </div>
          );
        },
      },
      {
        field: "delivery_salesman",
        headerName: "Delivery Created By",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "delivery_date",
        headerName: "Delivery Date",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) => {
          const date = params.row.delivery_date;
          if (date && moment(date, moment.ISO_8601, true).isValid()) {
            return moment(date).format("DD-MM-YYYY");
          } else {
            return null;
          }
        },
      },
      {
        field: "phone_number",
        headerName: translations[currentLanguage].phoneno,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "whatsapp_number",
        headerName: "WhatsApp Number",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "village_name",
        headerName: translations[currentLanguage].village,
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "taluka_name",
        headerName: translations[currentLanguage].taluka,
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "district_name",
        headerName: translations[currentLanguage].district,
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "manufacturer_name",
        headerName: "Manufacturer Name",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "new_modal",
        headerName: "Model Name",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "chassis_no",
        headerName: "Chassis No",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "engine_no",
        headerName: "Engine No",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "new_tractor_price",
        headerName: "New Tractor Price",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) =>
          `${new Intl.NumberFormat("en-IN", {
            maximumFractionDigits: 0,
          }).format(params.row.new_tractor_price || 0)}`,
      },
      {
        field: "old_details",
        headerName: "Old Tractors",
        headerAlign: "left",
        align: "left",
        minWidth: 250,
        flex: 1,
      },
      {
        field: "old_tractor_price",
        headerName: "Old Tractor Price",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) =>
          `${new Intl.NumberFormat("en-IN", {
            maximumFractionDigits: 0,
          }).format(params.row.old_tractor_price || 0)}`,
      },
      {
        field: "remaining_price",
        headerName: "Remaining Price",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) =>
          `${new Intl.NumberFormat("en-IN", {
            maximumFractionDigits: 0,
          }).format(params.row.remaining_price || 0)}`,
      },
      {
        field: "payment_mode",
        headerName: "Payment Method",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "payment_details",
        headerName: "Payment Details",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) => {
          const value = params.value;
          if (!value) return "";
          const parts = value.split(",").map((part) => part.trim()).filter(Boolean);
          const uniqueParts = [...new Set(parts)];
          // Join the unique parts back into a string.
          return uniqueParts.map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(", ")
        },
      },
      {
        field: "retail_date",
        headerName: "Retail Date",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) => {
          const date = params.row.retail_date;
          if (date && moment(date, moment.ISO_8601, true).isValid()) {
            return moment(date).format("DD/MM/YYYY");
          } else {
            return null;
          }
        },
      },
      {
        field: "sales_person_name",
        headerName: translations[currentLanguage].salesp,
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "created_by",
        headerName: "Created By",
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "primary_source",
        headerName: "Primary Source",
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "enquiry_source",
        headerName: "Enquiry Source",
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "enquiry_date",
        headerName: "Enquiry Date",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) => {
          const date = params.row.enquiry_date;
          if (date && moment(date, moment.ISO_8601, true).isValid()) {
            return moment(date).format("DD/MM/YYYY");
          } else {
            return null;
          }
        },
      },
      {
        field: "menu",
        headerName: (
          <FontAwesomeIcon icon={faEllipsisV} style={{ marginRight: "15px" }} />
        ),
        className: "bg-dark",
        sortable: false,
        filterable: false,
        headerAlign: "right",
        align: "right",
        disableColumnMenu: true,
        width: 200,
        position: "sticky",
        renderCell: (params) => (
          <div className="d-flex justify-content-center dotHoverempicon">
            <FontAwesomeIcon icon={faEllipsisV} />
            <div className="expandDiv expandDivenq">
              <Tooltip title="Edit">
                <button
                  onClick={() => {
                    editEnquiryCell(params.row);
                  }}
                  className="myActionBtn m-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    className="bi bi-pencil-square"
                    viewBox="0 0 16 16"
                  >
                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                    <path
                      fillRule="evenodd"
                      d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"
                    />
                  </svg>
                </button>
              </Tooltip>
              <Tooltip title="Delete">
                <button
                  onClick={() => {
                    deleteActionCall(params.row);
                  }}
                  className="myActionBtn m-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    className="bi bi-trash3"
                    viewBox="0 0 16 16"
                  >
                    <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
                  </svg>
                </button>
              </Tooltip>
              <Tooltip title="Move to Stage">
                <div
                  className="position-relative"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <button
                    className="myActionBtn m-1"
                    onClick={() => handleStageClick(ev_stage.type, params.row)}
                  >
                    <LabelImportantIcon />
                  </button>
                </div>
              </Tooltip>
            </div>
          </div>
        ),
      },
    ],
    INVALID: [
      {
        field: "first_name",
        headerAlign: "left",
        align: "left",
        headerName: translations[currentLanguage].firstname,
        minWidth: 310,
        flex: 1,
        renderCell: (params) => {
          const fistName = params.row.first_name || " ";
          return (
            <div
              className="text-uppercase"
              style={{
                textDecoration: "underline",
                color: "#000102",
                cursor: "pointer",
              }}
              onClick={() => {
                editEnquiryCell(params.row);
              }}
            >
              {fistName}
            </div>
          );
        },
      },
      {
        field: "sales_person_name",
        headerName: "Sales Person",
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "created_by",
        headerName: "Created By",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "village_name",
        headerName: translations[currentLanguage].village,
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "taluka_name",
        headerName: translations[currentLanguage].taluka,
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "district_name",
        headerName: translations[currentLanguage].district,
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "phone_number",
        headerName: translations[currentLanguage].phoneno,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "whatsapp_number",
        headerName: "WhatsApp Number",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "primary_source",
        headerName: "Primary Source",
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "enquiry_source",
        headerName: "Enquiry Source",
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "invalid_by",
        headerName: "Invalid By",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "enquiry_date",
        headerName: "Enquiry Date",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) => {
          const date = params.row.enquiry_date;
          if (date && moment(date, moment.ISO_8601, true).isValid()) {
            return moment(date).format("DD-MM-YYYY");
          } else {
            return null;
          }
        },
      },
      {
        field: "invalid_date",
        headerName: "Invalid Date",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) => {
          const date = params.row.invalid_date;
          if (date && moment(date, moment.ISO_8601, true).isValid()) {
            return moment(date).format("DD/MM/YYYY");
          } else {
            return null;
          }
        },
      },
      {
        field: "invalid_reason",
        headerName: "Invalid Reason",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },

      {
        field: "menu",
        headerName: (
          <FontAwesomeIcon icon={faEllipsisV} style={{ marginRight: "15px" }} />
        ),
        className: "bg-dark",
        sortable: false,
        filterable: false,
        headerAlign: "right",
        align: "right",
        disableColumnMenu: true,
        width: 200,
        position: "sticky",
        renderCell: (params) => (
          <div className="d-flex justify-content-center dotHoverempicon">
            <FontAwesomeIcon icon={faEllipsisV} />
            <div className="expandDiv expandDivenq">
              <Tooltip title="Edit">
                <button
                  onClick={() => {
                    editEnquiryCell(params.row);
                  }}
                  className="myActionBtn m-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    className="bi bi-pencil-square"
                    viewBox="0 0 16 16"
                  >
                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                    <path
                      fillRule="evenodd"
                      d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"
                    />
                  </svg>
                </button>
              </Tooltip>
              <Tooltip title="Delete">
                <button
                  onClick={() => {
                    deleteActionCall(params.row);
                  }}
                  className="myActionBtn m-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    className="bi bi-trash3"
                    viewBox="0 0 16 16"
                  >
                    <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
                  </svg>
                </button>
              </Tooltip>
              <Tooltip title="Move to Stage">
                <div
                  className="position-relative"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <button
                    className="myActionBtn m-1"
                    onClick={() => handleStageClick(ev_stage.type, params.row)}
                  >
                    <LabelImportantIcon />
                  </button>
                </div>
              </Tooltip>
            </div>
          </div>
        ),
      },
    ],
    LOST: [
      {
        field: "first_name",
        headerAlign: "left",
        align: "left",
        headerName: translations[currentLanguage].firstname,
        minWidth: 310,
        flex: 1,
        renderCell: (params) => {
          const fistName = params.row.first_name || " ";
          return (
            <div
              className="text-uppercase"
              style={{
                textDecoration: "underline",
                color: "#000102",
                cursor: "pointer",
              }}
            >
              {fistName}
            </div>
          );
        },
      },
      {
        field: "sales_person_name",
        headerName: translations[currentLanguage].salesp,
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "lost_date",
        headerName: "Lost Date",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) => {
          const date = params.row.lost_date;
          if (date && moment(date, moment.ISO_8601, true).isValid()) {
            return moment(date).format("DD-MM-YYYY");
          } else {
            return null;
          }
        },
      },
      {
        field: "enquiry_date",
        headerName: "Enquiry Date",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) => {
          const date = params.row.enquiry_date;
          if (date && moment(date, moment.ISO_8601, true).isValid()) {
            return moment(date).format("DD/MM/YYYY");
          } else {
            return null;
          }
        },
      },
      {
        field: "primary_source",
        headerName: "Primary Source",
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "enquiry_source",
        headerName: "Enquiry Source",
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },

      {
        field: "village_name",
        headerName: translations[currentLanguage].village,
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "taluka_name",
        headerName: translations[currentLanguage].taluka,
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "district_name",
        headerName: translations[currentLanguage].district,
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },

      {
        field: "phone_number",
        headerName: translations[currentLanguage].phoneno,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "whatsapp_number",
        headerName: "WhatsApp Number",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "manufacturer_name",
        headerName: "Lost Make Name",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "new_modal",
        headerName: "Lost New Model",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "new_tractor_price",
        headerName: "New Tractor Price",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) =>
          `${new Intl.NumberFormat("en-IN", {
            maximumFractionDigits: 0,
          }).format(params.row.new_tractor_price || 0)}`,
      },
      {
        field: "old_details",
        headerName: "Old Tractor",
        headerAlign: "left",
        align: "left",
        minWidth: 250,
        flex: 1,
      },
      {
        field: "old_tractor_price",
        headerName: "Old Tractor Price",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) =>
          `${new Intl.NumberFormat("en-IN", {
            maximumFractionDigits: 0,
          }).format(params.row.old_tractor_price || 0)}`,
      },
      {
        field: "remaining_price",
        headerName: "Remaining Price",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) =>
          `${new Intl.NumberFormat("en-IN", {
            maximumFractionDigits: 0,
          }).format(params.row.remaining_price || 0)}`,
      },
      {
        field: "payment_mode",
        headerName: "Payment Method",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "payment_details",
        headerName: "Payment Details",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "commercial_reasons",
        headerName: "Commercial Reasons 1",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "non_commercial_reasons",
        headerName: "Commercial Reasons 2",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "created_by",
        headerName: "Created By",
        headerAlign: "left",
        align: "left",
        type: "text",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "lost_by",
        headerName: "Lost By",
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "menu",
        headerName: (
          <FontAwesomeIcon icon={faEllipsisV} style={{ marginRight: "15px" }} />
        ),
        className: "bg-dark",
        sortable: false,
        filterable: false,
        headerAlign: "right",
        align: "right",
        disableColumnMenu: true,
        width: 200,
        position: "sticky",
        renderCell: (params) => (
          <div className="d-flex justify-content-center dotHoverempicon">
            <FontAwesomeIcon icon={faEllipsisV} />
            <div className="expandDiv expandDivenq">
              <Tooltip title="Edit">
                <button
                  className="myActionBtn m-1"
                  onClick={() => {
                    editEnquiryCell(params.row);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    className="bi bi-pencil-square"
                    viewBox="0 0 16 16"
                  >
                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                    <path
                      fillRule="evenodd"
                      d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"
                    />
                  </svg>
                </button>
              </Tooltip>
              <Tooltip title="Delete">
                <button
                  onClick={() => {
                    deleteActionCall(params.row);
                  }}
                  className="myActionBtn m-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    className="bi bi-trash3"
                    viewBox="0 0 16 16"
                  >
                    <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
                  </svg>
                </button>
              </Tooltip>
              <Tooltip title="Move to Stage">
                <div
                  className="position-relative"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <button
                    className="myActionBtn m-1"
                    onClick={() => handleStageClick(ev_stage.type, params.row)}
                  >
                    <LabelImportantIcon />
                  </button>
                </div>
              </Tooltip>
            </div>
          </div>
        ),
      },
    ],
    OTHER: [],
  });

  const getAllDates = (dates) => {
    if (!dates) return [];
    const [startDate, endDate] = dates.map((date) => new Date(date));
    const dateArr = [];
    const formatDate = (date) => {
      let year = date.getFullYear();
      let month = (date.getMonth() + 1).toString().padStart(2, "0");
      let day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    let currentDate = new Date(startDate);
    if (endDate === undefined) {
      dateArr.push(formatDate(currentDate));
      return dateArr;
    } else {
      while (currentDate <= endDate) {
        dateArr.push(formatDate(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return dateArr;
    }
  };

  const getEnquiries = async (categoryId, stagesDetails, date, timeType) => {
    let dateArr;
    if (timeType === "Custom") {
      dateArr = getAllDates(date);
    } else {
      dateArr = date;
    }
    const data = {
      dateArr: dateArr.join(","),
      ev_stage: stagesDetails.type,
      category: ev_stage.value || categoryId,
    };

    if (timeType === "All") {
      data.dateForm = "all";
    } else {
      data.dateForm = timeType;
    }
    const url1 = `${process.env.REACT_APP_NODE_URL}/api/${stagesDetails.endpoint}`;
    let url = "";
    url = url1;
    const config = {
      headers: {
        token: localStorage.getItem("rbacToken"),
      },
    };

    try {
      setLoading(true);
      const response = await axios.post(url, data, config);
      if (response.data.isSuccess) {
        console.log(response.data.result);
        const result = response.data.result;

        setExcelKey(ev_stage.type);
        if (result[ev_stage.type]) {
          // setEnquiries(result[ev_stage.type]);
          const customFields = result[ev_stage.type].map((custom) => {
            try {
              return custom.custom_fields
                ? JSON.parse(custom.custom_fields)
                : {};
            } catch {
              return {};
            }
          });
          const enrichedData = result[ev_stage.type].map((item) => {
            let customFields = {};
            try {
              customFields = item.custom_fields
                ? JSON.parse(item.custom_fields)
                : {};
            } catch (error) {
              console.error("Error parsing custom_fields:", error);
            }

            return {
              ...item,
              ...customFields,
            };
          });

          setEnquiries(enrichedData);

          let isType = ["DELIVERY", "INVALID", "LOST"];
          let isDataTypes = [];
          if (isType.includes(ev_stage.type)) {
            setIsAvailable((prev) => ({
              ...prev,
              [ev_stage.type]: true,
            }));

            const dynamicColumns = generateDynamicColumns(result.columns);

            const extractedColumns = extractRenderCellKeys(
              dynamicColumns,
              customFields,
              ev_stage.type
            );

            setExcelColumns(extractedColumns);
          } else if (isDataTypes.includes(ev_stage.type)) {
            setIsAvailable((prev) => ({
              ...prev,
              OTHER: true,
            }));
            const dynamicColumns = generateColumns(result.columns);
            const extractedColumns = extractRenderCellKeys(
              dynamicColumns,
              customFields,
              ev_stage.type
            );

            const typesColumns = generateColumns(extractedColumns);
            const allColumns = [
              { field: "indexID", headerName: "ID", width: 90 }, // Common ID column
              ...typesColumns, // Add dynamically generated columns after the common column
            ];

            setColumns((prevCol) => ({
              ...prevCol,
              OTHER: allColumns,
            }));

            setExcelColumns(extractedColumns);
          } else {
            setIsAvailable((prev) => ({
              ...prev,
              OTHER: true,
            }));
            const dynamicColumns = generateDynamicColumns(result.columns);
            const extractedColumns = extractRenderCellKeys(
              dynamicColumns,
              customFields,
              ev_stage.type
            );
            setExcelColumns(extractedColumns);
            const allColumns = [
              { field: "indexID", headerName: "ID", width: 90 }, // Common ID column
              ...dynamicColumns, // Add dynamically generated columns after the common column
            ];

            setColumns((prevCol) => ({
              ...prevCol,
              OTHER: allColumns,
            }));
          }
        }
      } else {
        setEnquiries([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching enquiries:", error);
      setLoading(false);
    }
  };

  // -----------------------------
  // Fetch Sales Data from API with Server-Side Filtering
  // -----------------------------
  const fetchSalesData = async (
    page = 0,
    pageSize = 10,
    dates,
    type,
    search = searchTerm
  ) => {
    setIsDataLoading(true);
    console.log(dates);
    if (!Array.isArray(dates) || dates.length === 0) {
      // console.error("Invalid date array");
      // return;
    }

    const customDates = getAllDates(dates);
    let startDate = "";
    let endDate = "";

    if (dates && dates.length > 0) {
      startDate = dates[0];
      endDate = dates[dates.length - 1];
    }

    if (type === "All") {
      startDate = "";
      endDate = "";
    } else if (type === "Custom") {
      startDate = customDates[0];
      endDate = customDates[customDates.length - 1];
    }

    try {
      const payload = {
        ev_stage: ev_stage.type,
        category: Category,
        page: page,
        limit: pageSize,
        searchQuery: search,
        ...(search.trim() === "" && { startDate, endDate }),
      };
      const response = await generalServices.getSalesData(payload);
      if (response && response.isSuccess) {
        const result = response.result;
        if (result && result[ev_stage.type]) {
          let enrichedData;
          if (ev_stage.type === "SSP") {
            // Flatten nested fields for SSP (IDs + raw custom_fields for edit)
            enrichedData = result[ev_stage.type].map((item) => ({
              ...item,
              primary_source: item.primary_source || "",
              enquiry_source: item.enquiry_source || "",
              farm_name: item.support_info?.farm_name || item.farm_name || "",
              remarks: item.support_info?.remarks || item.remarks || "",
              wark: item.support_info?.wark || item.wark || "",
              old_make: item.old_product?.old_make || item.old_make || "",
              old_model: item.old_product?.old_model || item.old_model || "",
              old_year: item.old_product?.old_year || item.old_year || "",
              old_price: item.old_product?.old_price || item.old_price || "",
              is_old_owned: item.old_product?.is_old_owned || "",
              custom_fields: item.custom_fields,
              district_id: item.district_id,
              taluka_id: item.taluka_id,
              village_id: item.village_id,
              ssp_id: item.ssp_id ?? item.id,
            }));
          } else {
            enrichedData = result[ev_stage.type].map((item) => {
              let customFields = {};
              try {
                customFields = item.custom_fields
                  ? JSON.parse(item.custom_fields)
                  : {};
              } catch (error) {
                console.error("Error parsing custom_fields:", error);
              }
              return {
                ...item,
                ...customFields,
              };
            });
          }
          const customFields = result[ev_stage.type].map((custom) => {
            try {
              return custom.custom_fields
                ? JSON.parse(custom.custom_fields)
                : {};
            } catch {
              return {};
            }
          });

          setExcelKey(ev_stage.type);
          setTotalCount(result.totalCount);
          let isType = ["DELIVERY", "INVALID", "LOST"];
          if (isType.includes(ev_stage.type)) {
            if (isType.includes("DELIVERY")) {
              enrichedData.sort(
                (a, b) => moment(b.delivery_date) - moment(a.delivery_date)
              );
            }

            setEnquiries(enrichedData);
            const dynamicColumns = generateDynamicColumns(result.columns);

            const extractedColumns = extractRenderCellKeys(
              dynamicColumns,
              customFields,
              ev_stage.type
            );
            setColumns((prevCol) => ({
              ...prevCol,
              OTHER: columns[ev_stage.type],
            }));
            setExcelColumns(extractedColumns);
          } else {
            setEnquiries(enrichedData);
            const uniqueDynamicColumns = generateDynamicColumns(result.columns);
            const dynamicColumns = [
              ...new Map(
                uniqueDynamicColumns.map((col) => [col.field, col])
              ).values(),
            ];
            const extractedColumns = extractRenderCellKeys(
              dynamicColumns,
              customFields,
              ev_stage.type
            );
            setExcelColumns(extractedColumns);
            const allColumns = [
              // { field: "indexID", headerName: "ID", width: 90 },
              ...dynamicColumns, // Add dynamically generated columns after the common column
            ];

            setColumns((prevCol) => ({
              ...prevCol,
              OTHER: allColumns,
            }));
          }
        } else {
          setEnquiries([]);
          setTotalCount(0); // Added to clear pagination count
        }
      } else {
        setEnquiries([]);
        setTotalCount(0); // Added to clear pagination count
      }
    } catch (error) {
      console.error("Error fetching sales data:", error);
      setEnquiries([]);
      setTotalCount(0); // Added to clear pagination count
    } finally {
      setIsDataLoading(false);
    }
  };


  // Fetch data when page or pageSize changes
  useEffect(() => {
    console.log("page Changesed", page, pageSize);
    fetchSalesData(page, pageSize, selectedDates, selectedType, searchTerm);
  }, [page, pageSize]);

  // Fetch data when searchTerm changes (with debounce)
  useEffect(() => {
    const timeout = setTimeout(() => {
      // setPage(0);
      fetchSalesData(page, pageSize, selectedDates, selectedType, searchTerm);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Initialize data on component mount ONLY if filter state was restored from sessionStorage
  useEffect(() => {
    // Check if we have saved filter state from previous session
    const hasSavedState = sessionStorage.getItem("lostEnquiry_selectedDates") ||
      sessionStorage.getItem("lostEnquiry_selectedType");

    // Only auto-fetch if: (1) we have saved filters AND (2) no data loaded yet AND (3) not currently loading
    if (hasSavedState && rowData.length === 0 && !isDataLoading) {
      console.log("Restoring previous filter state and fetching data...");
      // Use the already-restored selectedDates and selectedType from state initialization
      fetchSalesData(0, pageSize, selectedDates, selectedType, searchTerm);
    }
  }, []);

  // Persist filter state to sessionStorage when selectedDates or selectedType changes
  useEffect(() => {
    try {
      sessionStorage.setItem("lostEnquiry_selectedDates", JSON.stringify(selectedDates));
      sessionStorage.setItem("lostEnquiry_selectedType", selectedType);
    } catch (error) {
      console.error("Error saving filter state to sessionStorage:", error);
    }
  }, [selectedDates, selectedType]);

  const getFields = (field) => {
    const fieldMapping = {
      branchId: "branch_name",
      firstName: "first_name",
      lastName: "last_name",
      email: "email",
      mobileNumber: "phone_number",
      whatsappNumber: "whatsapp_number",
      district: "district",
      taluko: "taluka",
      village: "village",
      dsp: "sales_person",
      manufature: "manufacturer_name",
      modal: "product",
      engineNumber: "engine_no",
      chasisNumber: "chassis_no",
      year: "year",
      hour: "hour",
      newPrice: "new_tractor_price",
      isOldOwned: "isOldOwned",
      oldmake: "oldmake",
      oldmodel: "oldmodel",
      oldyear: "oldyear",
      oldPrice: "old_tractor_price",
      totalHours: "total_hours",
      tractorNo: "tractor_no",
      oldCondition: "old_condition",
      paymentMethod: "payment_mode",
      paymentDetail: "payment_details",
      bank: "bankname",
      remainingPrice: "remaining_price",
      primarySource: "primary_source",
      enquirySource: "enquiry_source",
      enquiryDate: "enquiry_date",
      deliveryDate: "delivery_date",
    };

    return fieldMapping[field] || field; // Default to field name if no mapping is found
  };

  function extractRenderCellKeys(dynamicColumns, customFields, ev_stage) {
    const extractedColumns = [];

    // Exclude the last item using slice
    const filteredColumns = dynamicColumns.slice(0, -1);
    filteredColumns.forEach((column) => {
      extractedColumns.push({
        name: column.headerName,
        column: column.field,
        isChecked: false,
      });
    });

    if (ev_stage === "DELIVERY") {
      // Stage Columns
      let deliveryColumns = [
        {
          name: "Delivery Created By",
          column: "delivery_salesman",
          isChecked: false,
        },
        {
          name: "Delivery Created Date",
          column: "delivery_date",
          isChecked: false,
        },
        { name: "First Name", column: "first_name", isChecked: false },
        { name: "Phone Number", column: "phone_number", isChecked: false },
        {
          name: "Whatsapp Number",
          column: "whatsapp_number",
          isChecked: false,
        },
        { name: "Village", column: "village_name", isChecked: false },
        { name: "Taluko", column: "taluka_name", isChecked: false },
        { name: "District", column: "district_name", isChecked: false },
        {
          name: "Manufacturers Name",
          column: "manufacturer_name",
          isChecked: false,
        },
        { name: "New Model", column: "new_modal", isChecked: false },
        { name: "Chassis Number", column: "chassis_no", isChecked: false },
        { name: "Engine Number", column: "engine_no", isChecked: false },
        {
          name: "New Tractor Price",
          column: "new_tractor_price",
          isChecked: false,
        },
        { name: "Old Tractor", column: "old_details", isChecked: false },
        {
          name: "Old Tractor Price",
          column: "old_tractor_price",
          isChecked: false,
        },
        {
          name: "Remaining Price",
          column: "remaining_price",
          isChecked: false,
        },
        { name: "Payment Methods", column: "payment_mode", isChecked: false },
        {
          name: "Payment Details",
          column: "payment_details",
          isChecked: false,
        },
        { name: "Retail Date", column: "retail_date", isChecked: false },
        { name: "Sales Person", column: "sales_person_name", isChecked: false },
        { name: "Created By", column: "created_by", isChecked: false },
        { name: "Stage Name", column: "stage_name", isChecked: false },
        { name: "Primary Source", column: "primary_source", isChecked: false },
        { name: "Enquiry Source", column: "enquiry_source", isChecked: false },
        { name: "Enquiry Date", column: "enquiry_date", isChecked: false },
      ];

      return deliveryColumns;
    }
    if (ev_stage === "LOST") {
      // Stage Columns
      let lostColumns = [
        { name: "Sales Person", column: "sales_person_name", isChecked: false },
        { name: "First Name", column: "first_name", isChecked: false },
        { name: "Lost Date", column: "lost_date", isChecked: false },
        { name: "Enquiry Date", column: "enquiry_date", isChecked: false },
        { name: "Primary Source", column: "primary_source", isChecked: false },
        { name: "Enquiry Source", column: "enquiry_source", isChecked: false },
        { name: "Village", column: "village_name", isChecked: false },
        { name: "Taluko", column: "taluka_name", isChecked: false },
        { name: "District", column: "district_name", isChecked: false },
        { name: "Phone Number", column: "phone_number", isChecked: false },
        {
          name: "Whatsapp Number",
          column: "whatsapp_number",
          isChecked: false,
        },
        {
          name: "Lost Make Name",
          column: "manufacturer_name",
          isChecked: false,
        },
        { name: "Lost New Model", column: "new_modal", isChecked: false },
        {
          name: "New Tractor Price",
          column: "new_tractor_price",
          isChecked: false,
        },
        { name: "Old Tractor", column: "old_details", isChecked: false },
        {
          name: "Old Tractor Price",
          column: "old_tractor_price",
          isChecked: false,
        },
        {
          name: "Remaining Price",
          column: "remaining_price",
          isChecked: false,
        },
        { name: "Payment Methods", column: "payment_mode", isChecked: false },
        {
          name: "Payment Details",
          column: "payment_details",
          isChecked: false,
        },
        {
          name: "Commercial Reasons 1",
          column: "commercial_reasons",
          isChecked: false,
        },
        {
          name: "Commercial Reasons 2",
          column: "non_commercial_reasons",
          isChecked: false,
        },
        { name: "Created By", column: "created_by", isChecked: false },
        {
          name: "Follow Up Details",
          column: "followup_details",
          isChecked: false,
        },
        { name: "Lost By", column: "lost_by", isChecked: false },
      ];

      return lostColumns;
    }
    if (ev_stage === "INVALID") {
      // Stage Columns
      let invalidColumns = [
        { name: "Sales Person", column: "sales_person_name", isChecked: false },
        { name: "First Name", column: "first_name", isChecked: false },
        { name: "Created By", column: "created_by", isChecked: false },
        { name: "Village", column: "village_name", isChecked: false },
        { name: "Taluko", column: "taluka_name", isChecked: false },
        { name: "District", column: "district_name", isChecked: false },
        { name: "Phone Number", column: "phone_number", isChecked: false },
        {
          name: "Whatsapp Number",
          column: "whatsapp_number",
          isChecked: false,
        },
        { name: "Primary Source", column: "primary_source", isChecked: false },
        { name: "Enquiry Source", column: "enquiry_source", isChecked: false },

        { name: "Enquiry Date", column: "date", isChecked: false },
        { name: "Invalid Date", column: "invalid_date", isChecked: false },
        { name: "Invalid By", column: "invalid_by", isChecked: false },
        { name: "Invalid Reason", column: "invalid_reason", isChecked: false },
      ];

      return invalidColumns;
    }
    return [...extractedColumns];
  }

  const generateColumns = (columnsData) => {
    const dynamicColumns = columnsData.map((field) => {
      const baseConfig = {
        field: field.column,
        headerName: field.name,
        minWidth: 200,
        flex: 1,
        headerAlign: "left",
        align: "left",
      };

      return {
        ...baseConfig,
      };
    });

    const menuColumn = {
      field: "menu",
      headerName: (
        <FontAwesomeIcon icon={faEllipsisV} style={{ marginRight: "15px" }} />
      ),
      className: "bg-dark",
      sortable: false,
      filterable: false,
      headerAlign: "right",
      align: "right",
      disableColumnMenu: true,
      width: 200,
      renderCell: (params) => (
        <div className="d-flex justify-content-center dotHoverempicon">
          <FontAwesomeIcon icon={faEllipsisV} />
          <div className="expandDiv expandDivenq">
            <Tooltip title="Edit">
              <button
                className="myActionBtn m-1"
                onClick={() => editEnquiryCell(params.row)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-pencil-square"
                  viewBox="0 0 16 16"
                >
                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                  <path
                    fillRule="evenodd"
                    d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"
                  />
                </svg>
              </button>
            </Tooltip>
            <Tooltip title="Delete">
              <button
                onClick={() => deleteActionCall(params.row)}
                className="myActionBtn m-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-trash3"
                  viewBox="0 0 16 16"
                >
                  <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
                </svg>
              </button>
            </Tooltip>
          </div>
        </div>
      ),
    };

    return [...dynamicColumns, menuColumn];
  };

  // Function to generate dynamic columns based on the data
  const generateDynamicColumns = (columnsData) => {
    const dynamicColumns = columnsData.map((field) => {
      const baseConfig = {
        field: getFields(field.name),
        headerName: field.label,
        // minWidth: 310,
        minWidth: field.name === "firstName" ? 310 : 200,
        flex: 1,
        headerAlign: "left",
        align: "left",
      };
      if (field.name === "deliveryDate") {
        baseConfig.valueGetter = (params) => {
          const date = params.row.delivery_date;
          return date && moment(date, moment.ISO_8601, true).isValid()
            ? moment(date).format("DD-MM-YYYY")
            : null;
        };
      }
      return {
        ...baseConfig,
      };
    });

    const menuColumn = {
      field: "menu",
      headerName: (
        <FontAwesomeIcon icon={faEllipsisV} style={{ marginRight: "15px" }} />
      ),
      className: "bg-dark",
      sortable: false,
      filterable: false,
      headerAlign: "right",
      align: "right",
      disableColumnMenu: true,
      width: 200,
      renderCell: (params) => (
        <div className="d-flex justify-content-center dotHoverempicon">
          <FontAwesomeIcon icon={faEllipsisV} />
          <div className="expandDiv expandDivenq">
            <Tooltip title="Edit">
              <button
                className="myActionBtn m-1"
                onClick={() => editEnquiryCell(params.row)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-pencil-square"
                  viewBox="0 0 16 16"
                >
                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                  <path
                    fillRule="evenodd"
                    d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"
                  />
                </svg>
              </button>
            </Tooltip>
            <Tooltip title="Delete">
              <button
                onClick={() => deleteActionCall(params.row)}
                className="myActionBtn m-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-trash3"
                  viewBox="0 0 16 16"
                >
                  <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
                </svg>
              </button>
            </Tooltip>
          </div>
        </div>
      ),
    };

    return [...dynamicColumns, menuColumn];
  };

  useEffect(() => {
    if (enquiries.length > 0) {
      const rowsData = enquiries.map((item, index) => ({
        ...item,
        indexID: index + 1,
      }));
      setRowData(rowsData);
    } else {
      setRowData([]); // Added to clear the grid when there is no data
    }
  }, [enquiries]);

  const fetchEnquiryTypeUrl = (type) => {
    switch (type) {
      case "LOST":
        return {
          type: type,
          endpoint: "general/get-enquiries-by-dates",
        };
      case "INVALID":
        return {
          type: type,
          endpoint: "general/get-enquiries-by-dates",
        };
      case "DELIVERY":
        return {
          type: type,
          endpoint: "general/get-enquiries-by-dates",
        };
      default:
        return { type: type, endpoint: "general/get-enquiries-by-dates" };
    }
  };

  const handleCategoryChange = async (category) => {
    console.log(category, "Category->>>>>");
    if (category !== "Not") {
      setCategory(category.value);
    }
  };

  const handleApplyButton = async (dates, type, category) => {
    setSelectedType(type);
    setSelectedDates(dates);
    await fetchSalesData(page, pageSize, dates, type, searchTerm);
  };

  const onChangeXLSXFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log(file, "selected xlsx file");
      setXlsxFile(file);
      // Automatically trigger upload when a file is selected
      handleXlSXUpload(file);
    } else {
      setXlsxFile(null);
    }
  };

  const handleXlSXUpload = async (fileOverride) => {
    const fileToUpload = fileOverride || xlsxFile;
    if (!fileToUpload) {
      console.log("No XLSX file selected for upload");
      return;
    }

    console.log(fileToUpload, "csdielf");
    // Pass current stage (e.g. DELIVERY / INVALID / LOST) so the
    // backend can set enquiry_stage appropriately for uploaded rows.
    const url = `${process.env.REACT_APP_NODE_URL}/api/upload-xls?categoryId=${Category}&stage=${ev_stage.type}`;
    const config = {
      headers: {
        token: localStorage.getItem("rbacToken"),
      },
    };
    const formData = new FormData();
    formData.append("enquiryxlxs", fileToUpload);

    try {
      const response = await axios.post(url, formData, config);
      if (response.data && response.data.message === "Xlxs Success") {
        console.log(response.data, "UPLOAD SUCCESS");
        alert("Data successfully inserted from Excel.");
        await fetchSalesData(page, pageSize, selectedDates, selectedType, searchTerm);
      } else if (response.data && response.data.error === "Xlxs Failed") {
        alert("Excel upload failed.");
      }
    } catch (error) {
      console.error("Error uploading XLSX:", error);
      alert("Error uploading XLSX file.");
    }
  };

  const downloadFile = async () => {
    try {
      // Include current stage so backend can generate
      // a delivery-specific sample when needed.
      const url = `${process.env.REACT_APP_NODE_URL}/api/enquiry/download-file?categoryId=${Category}&stage=${ev_stage.type}`;
      console.log(url, "DOWNLOAD SAMPLE URL");

      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading sample file:", error.message);
      alert("Error downloading sample file.");
    }
  };

  return (
    <div className="container-fluid p-0 m-0">
      <div className="d-flex justify-content-between align-items-center"></div>
      <ToolPanel
        isFileUploadEnabled={true}
        isAction={true}
        handleApplyButton={handleApplyButton}
        handleCategoryChange={
          isType.includes(ev_stage.type) ? handleCategoryChange : undefined
        }
        ExcelSetup={{
          selectedDates,
          selectedType,
          category: Category,
          excelKey,
          excelColumns: excelColumns,
        }}
        onFileChange={onChangeXLSXFile}
        onUpload={handleXlSXUpload}
        onGet={downloadFile}
        onAdd={
          ev_stage?.type === "SSP"
            ? () =>
                navigate("/sales/add", {
                  state: { preselectCategoryId: Category },
                })
            : undefined
        }
        onBack={() => {
          navigate("/sale/data");
        }}
      />

      {modalData.show && (
        <DeleteBox
          showModal={modalData.show}
          onClose={() =>
            setModalData({ show: false, rowData: null, endPoint: null })
          }
          deleteID={modalData.rowData.id}
          entityLabel={`${modalData.rowData.first_name}`}
          endPoint={modalData.endPoint}
          onResponse={onDeleteResponse}
        />
      )}
      <MovetoStageMenu
        showModal={MovetoStageModal.show}
        onClose={() =>
          setMovetoStageModal({ show: false, rowData: null, stages: [] })
        }
        stages={MovetoStageModal.stages}
        modalData={MovetoStageModal.rowData}
        handleStageMove={handleStageMove}
      />
      <div
        className="container-fluid bg-white rounded p-2 mb-3"
        style={{ position: "relative", width: "100%", maxWidth: "100vw" }}
      >
        <div style={{ position: "absolute", zIndex: 1, width: "400px" }}>
          <TextField
            type="text"
            placeholder="Search..."
            name="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "400px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "4px",
                height: "30px",
                "& fieldset": { border: "1px solid #E0E0E0" },
              },
            }}
            variant="outlined"
            size="small"
          />
        </div>
        <div
          className="tableMenuHover"
          style={{ flexGrow: 1, width: "100%", height: "calc(100vh - 70px)" }}
        >
          <PersistentDataGrid
            key={ev_stage?.type} // ADDED KEY FOR UNIQUE INSTANCE PER STAGE
            rows={rowData}
            columns={[
              {
                field: "id",
                headerName: "No",
                width: 80,
                valueGetter: (params) => {
                  const index = params.api.getAllRowIds().indexOf(params.id);
                  // Logic: (Current Page Index * Page Size) + Row Index + 1
                  return (page * pageSize) + index + 1;
                },
              },
              ...getStageColumns(),
              ...(ev_stage?.type === "SSP" ? [sspActionsColumn] : []),
            ]}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            getRowId={(row) => row.id}
            pagination
            paginationMode="server"
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}

            pageSizeOptions={[10, 15, 25, 50]}
            rowsPerPageOptions={[10, 15, 25, 50, 100]}
            checkboxSelection={false}
            disableSelectionOnClick

            // --- CUSTOM TOOLBAR APPLIED HERE ---
            components={{
              Toolbar: CustomToolbar,
              NoRowsOverlay: () => (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                  }}
                >
                  <span style={{ fontFamily: "Poppins" }}>
                    No Data Available
                  </span>
                </div>
              ),
            }}
            // ------------------------------------

            // --- PASS PROPS TO EXTERNAL COMPONENT ---
            componentsProps={{
              toolbar: {
                onExportFunc: handleExportAll, // Pass the export function
                isDataLoading: isDataLoading,
                totalCount: totalCount,
                position: "right",
                style: { fontFamily: "Poppins", alignSelf: "end" },
              },
            }}
            sx={{ width: "100%" }}
            loading={isDataLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default EnquiryStage;